import {
  createGrid,
  GridOptions,
  CellValueChangedEvent,
} from "ag-grid-enterprise";
import { AgGridBaseController } from "../../application/toolkit/ag_grid_base_component/ag_grid_base_controller";
import {
  EB_EDITABLE_HEADER_HEIGHT,
  EB_EDITABLE_ROW_HEIGHT,
  ebEditableTheme,
} from "../../application/toolkit/ag_grid_shared/eb_theme";
import { ebLocaleText } from "../../application/toolkit/ag_grid_shared/filter_defaults";
import { NO_CLIENT_EXPORT } from "../../application/toolkit/ag_grid_shared/no_client_export";
import { applyErrorFieldCellClassRules } from "../../application/toolkit/ag_grid_shared/error_field_cell_class_rules";
import { csrfToken } from "../../application/toolkit/ag_grid_shared/grid_edit_queue";

type ReviewRow = Record<string, unknown>;

export const ERRORS_COL_ID = "errors";

// Reported to the page controller, which owns the only status line the admin can read — same
// contract as the typing grid's own append-voters-grid:cell-error.
export const SAVE_ERROR_EVENT = "append-voters-review-grid:error";

export const SAVE_ERROR_MESSAGE = "Could not save the change. Please retry.";

// A saved edit clears the row's own verdict server-side, which makes the whole list's "valid"
// stale. Announced only once the POST succeeded — a reverted edit changed nothing.
export const CHANGED_EVENT = "append-voters-review-grid:changed";

// One page of the overview is 100 rows; the box shows a screenful and scrolls the rest, the same
// way the table it replaced did.
export const MAX_VISIBLE_ROWS = 10;
export const MIN_VISIBLE_ROWS = 3;

export const TOUCH_ROW_HEIGHT = 44;

const FALLBACK_VERTICAL_BORDERS = 2;

// One below breakpoint.scss's $screen-tablet, so the grid and the page CSS switch layouts at the
// same width.
export const NARROW_MAX_WIDTH = 639;

const RESIZE_DEBOUNCE_MS = 150;

export function visibleRowsFor(rowCount: number): number {
  return Math.min(Math.max(rowCount, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS);
}

export default class extends AgGridBaseController {
  static values = {
    ...AgGridBaseController.values,
    rowData: Array,
    updateUrl: String,
  };

  declare readonly rowDataValue: ReviewRow[];

  declare readonly updateUrlValue: string;

  // Reverting a rejected edit writes the cell again, which re-enters onCellValueChanged — without
  // this the failed POST would be retried with the old value.
  private revertingCells = new Set<string>();

  private reviewResizeHandler: (() => void) | null = null;

  private reviewResizeTimer: number | null = null;

  connect() {
    super.connect();

    if (!this.hasGridTarget) return;

    const options: GridOptions = {
      theme: ebEditableTheme,
      localeText: ebLocaleText,
      columnDefs: applyErrorFieldCellClassRules(this.columnDefsValue),
      rowData: this.rowDataValue,
      domLayout: "normal",
      suppressMovableColumns: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowDrag: true,
      rowGroupPanelShow: "never",
      // The same handle the typing grid has. Both tables are edited the same way and by the same
      // organizer; a drag that fills a column on one and does nothing on the other reads as broken.
      cellSelection: { handle: { mode: "fill" } },
      // AG Grid fills numbers as a series by default, so dragging an ID of 4 writes 5, 6, 7.
      // A voter list is typed the way a spreadsheet is: the drag repeats what was entered.
      fillOperation: (params) => params.initialValues[params.values.length % params.initialValues.length],
      rowNumbers: { width: 50, minWidth: 50 },
      stopEditingWhenCellsLoseFocus: true,
      suppressContextMenu: true,
      ...(this.coarsePointer ? { rowHeight: TOUCH_ROW_HEIGHT, singleClickEdit: true } : {}),
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
      },
      onCellValueChanged: (event: CellValueChangedEvent) => this.persistCell(event),
      onFirstDataRendered: () => this.fitColumns(),
      popupParent: document.body,
      ...this.gridOptionsValue,
      ...NO_CLIENT_EXPORT,
    };

    this.gridApi = createGrid(this.gridTarget, options);

    this.sizeBoxToRows();

    this.reviewResizeHandler = () => this.scheduleResize();
    window.addEventListener("resize", this.reviewResizeHandler);
    window.addEventListener("orientationchange", this.reviewResizeHandler);
  }

  disconnect() {
    if (this.reviewResizeHandler) {
      window.removeEventListener("resize", this.reviewResizeHandler);
      window.removeEventListener("orientationchange", this.reviewResizeHandler);
      this.reviewResizeHandler = null;
    }
    if (this.reviewResizeTimer !== null) {
      window.clearTimeout(this.reviewResizeTimer);
      this.reviewResizeTimer = null;
    }
    super.disconnect();
  }

  switchToBasic(): void {}

  switchToAdvanced(): void {}

  private get coarsePointer(): boolean {
    return window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  }

  private get rowHeight(): number {
    return this.coarsePointer ? TOUCH_ROW_HEIGHT : EB_EDITABLE_ROW_HEIGHT;
  }

  private persistCell(event: CellValueChangedEvent): void {
    const colId = event.column.getColId();
    if (colId === ERRORS_COL_ID) return;

    const lineId = (event.data as ReviewRow).id;
    if (lineId === null || lineId === undefined) return;

    const cellKey = `${lineId}:${colId}`;
    if (this.revertingCells.has(cellKey)) {
      this.revertingCells.delete(cellKey);
      return;
    }

    const newValue = event.newValue === null || event.newValue === undefined ? "" : String(event.newValue);
    const oldValue = event.oldValue;
    if (newValue === (oldValue === null || oldValue === undefined ? "" : String(oldValue))) return;

    fetch(this.updateUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({
        voter_line_validation_id: lineId,
        attribute_name: colId,
        attribute_value: newValue,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          this.revertCell(event, cellKey);
          return;
        }
        this.element.dispatchEvent(new CustomEvent(CHANGED_EVENT, { bubbles: true }));
      })
      .catch(() => this.revertCell(event, cellKey));
  }

  private revertCell(event: CellValueChangedEvent, cellKey: string): void {
    this.revertingCells.add(cellKey);
    event.node.setDataValue(event.column.getColId(), event.oldValue);
    this.element.dispatchEvent(
      new CustomEvent(SAVE_ERROR_EVENT, { bubbles: true, detail: { message: SAVE_ERROR_MESSAGE } }),
    );
  }

  private scheduleResize(): void {
    if (this.reviewResizeTimer !== null) window.clearTimeout(this.reviewResizeTimer);

    this.reviewResizeTimer = window.setTimeout(() => {
      this.reviewResizeTimer = null;
      this.sizeBoxToRows();
      this.fitColumns();
    }, RESIZE_DEBOUNCE_MS);
  }

  private sizeBoxToRows(): void {
    const rows = visibleRowsFor(this.rowDataValue.length) * this.rowHeight;

    this.gridTarget.style.height = `${EB_EDITABLE_HEADER_HEIGHT + rows + this.verticalBorders()}px`;
  }

  private verticalBorders(): number {
    const wrapper = this.gridTarget.querySelector(".ag-root-wrapper");
    const style = wrapper && window.getComputedStyle?.(wrapper);
    if (!style) return FALLBACK_VERTICAL_BORDERS;

    const top = parseFloat(style.borderTopWidth);
    const bottom = parseFloat(style.borderBottomWidth);
    if (Number.isNaN(top) || Number.isNaN(bottom)) return FALLBACK_VERTICAL_BORDERS;

    return top + bottom;
  }

  // A phone scrolls the columns horizontally instead, the way the table it replaced did.
  private fitColumns(): void {
    if (window.innerWidth <= NARROW_MAX_WIDTH) return;

    this.gridApi?.sizeColumnsToFit();
  }
}
